import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { useLanguage } from "../i18n";
import { Reveal } from "../components/Reveal";

export default function ResetPassword() {
  const { t } = useLanguage();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error(t("auth.passwordsMismatch"));
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("misc.error"));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm outline-none focus:border-[#FF6B35] transition-colors";

  return (
    <div data-testid="reset-password-page" className="max-w-md mx-auto py-16 px-4">
      <Reveal>
        <div className="rounded-3xl bg-white border border-slate-200/80 shadow-sm p-8 sm:p-10">
          {done ? (
            <div className="text-center" data-testid="reset-done">
              <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500" />
              <h1 className="font-display font-bold text-2xl text-slate-900 mt-5">{t("auth.resetDone")}</h1>
              <Link to="/account" data-testid="reset-goto-login"
                className="inline-block mt-6 rounded-full bg-[#FF6B35] text-white px-8 py-3 font-mono-t text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#E8551F] transition-colors">
                {t("auth.backToLogin")}
              </Link>
            </div>
          ) : (
            <>
              <h1 className="font-display font-extrabold text-3xl tracking-tight text-slate-900">{t("auth.resetTitle")}</h1>
              <p className="mt-2 text-sm text-slate-600">{t("auth.resetSub")}</p>
              <form onSubmit={submit} className="mt-8 space-y-4">
                <input data-testid="reset-password-input" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t("auth.newPassword")} className={inputCls} />
                <input data-testid="reset-confirm-input" type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={t("auth.confirmPassword")} className={inputCls} />
                <button data-testid="reset-submit" type="submit" disabled={busy || !token}
                  className="w-full rounded-full bg-[#FF6B35] text-white px-6 py-3.5 font-mono-t text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#E8551F] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                  {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                  {t("auth.resetTitle")}
                </button>
              </form>
            </>
          )}
        </div>
      </Reveal>
    </div>
  );
}
